import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fetchBlobUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import PlayerProfileModal from "@/components/PlayerProfileModal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Pencil, UserPlus, Trash2, Clock, UserCheck, AlertOctagon, Activity, AlertTriangle, Ban, Cake } from "lucide-react";
import { validPositionsFor, validPositionsForCategory } from "@/lib/positions";
import { isAdminLike, isStaff } from "@/lib/roles";
import { calcAge } from "@/lib/dates";

const CAT_LABELS = {
  tackle: "Tackle",
  flag_masculino: "Flag Masculino",
  flag_femenino: "Flag Femenino",
};

const STATUS_LIST = [
  { value: "active", label: "Activo", icon: Activity, color: "#34C759" },
  { value: "injured", label: "Lesionado", icon: AlertTriangle, color: "#FF9F0A" },
  { value: "suspended", label: "Suspendido", icon: Ban, color: "#FF3B30" },
];

export default function Roster() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [pending, setPending] = useState([]);
  const [uncategorized, setUncategorized] = useState([]);
  const [photos, setPhotos] = useState({});
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const loadPlayers = async () => {
    // Solo admin-like puede ver pending. Coach obtendría 403 — evitamos la
    // llamada y dejamos pending=[] para que la UI no muestre la sección.
    const canSeePending = isAdminLike(user?.role);
    const [pl, pd, uc] = await Promise.all([
      api.get("/players"),
      canSeePending ? api.get("/users/pending").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      api.get("/players/uncategorized").catch(() => ({ data: [] })),
    ]);
    setPlayers(pl.data);
    setPending(pd.data);
    setUncategorized(uc.data);
    for (const p of pl.data) {
      if (photos[p.user_id]) continue;
      const driveId = p.profile?.photo_drive_id;
      const legacyPath = p.profile?.photo_path;
      let url = null;
      if (driveId) {
        url = await fetchBlobUrl(`/drive/file/${driveId}`);
      } else if (legacyPath) {
        url = await fetchBlobUrl(`/files/${legacyPath}`);
      }
      if (url) setPhotos(prev => ({ ...prev, [p.user_id]: url }));
    }
  };

  useEffect(() => { loadPlayers(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return players;
    return players.filter(p => (p.profile?.categories || []).includes(filter));
  }, [players, filter]);

  const counts = useMemo(() => {
    const c = { all: players.length, tackle: 0, flag_masculino: 0, flag_femenino: 0 };
    players.forEach(p => {
      (p.profile?.categories || []).forEach(cat => { if (c[cat] !== undefined) c[cat]++; });
    });
    return c;
  }, [players]);

  if (!isStaff(user?.role)) {
    return <Layout><div className="p-10 text-[#A1A1AA]">Sin acceso.</div></Layout>;
  }

  const canEdit = isStaff(user?.role);
  const canDelete = isAdminLike(user?.role);
  // Solo perfiles admin (super_admin, admin_directive, admin_treasurer) pueden
  // aprobar nuevas solicitudes o agregar manualmente integrantes nuevos. Los
  // coaches sólo gestionan categorías y datos del plantel ya aprobado.
  const canApprove = isAdminLike(user?.role);

  // Quién puede abrir el modal de detalle personal de un jugador:
  // Política interna: SOLO admin-like accede a información personal
  // (RUT, teléfono, contacto emergencia, info médica, edad, email). Los
  // coaches se limitan a gestionar la información deportiva vía el editor
  // inline del card.
  const canViewProfile = () => isAdminLike(user?.role);

  const openProfile = (p) => {
    if (!canViewProfile(p)) {
      toast.error("No tienes acceso al perfil de este jugador");
      return;
    }
    setSelectedPlayer(p);
  };

  const approveAs = async (uid, role) => {
    await api.patch(`/users/${uid}/role`, { role });
    toast.success(`Aprobado como ${role === "player" ? "Jugador" : "Coach"}`);
    loadPlayers();
  };

  const deletePlayer = async (uid, name) => {
    if (!confirm(`¿Eliminar a ${name}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Jugador eliminado");
      loadPlayers();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px]">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline">Squad</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">ROSTER</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">Plantel completo y fichas individuales por categoría.</p>
          </div>
          {canApprove && (
            <button onClick={() => setShowAdd(true)} className="btn-primary text-xs flex items-center gap-2" data-testid="add-player-btn">
              <UserPlus size={14}/> Agregar jugador
            </button>
          )}
        </div>

        {showAdd && <AddPlayerForm onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadPlayers(); }}/>}

        {/* Solicitudes pendientes — SOLO admin-like (super_admin/directive/treasurer) */}
        {canApprove && pending.length > 0 && (
          <div className="tactical-card p-5 mb-6" style={{ borderColor: "#FF9F0A", background: "linear-gradient(120deg, rgba(255,159,10,0.06) 0%, transparent 50%)" }} data-testid="pending-requests">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#FF9F0A]"/>
                <div className="overline text-[#FF9F0A]">Solicitudes pendientes ({pending.length})</div>
              </div>
              <div className="text-[10px] text-[#71717A] uppercase tracking-widest">Aprueba para que ingresen al equipo</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pending.map(u => (
                <div key={u.user_id} className="flex items-center justify-between gap-3 border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={`pending-${u.user_id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-10 h-10 object-cover flex-shrink-0"/>
                    ) : (
                      <div className="w-10 h-10 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white font-bold flex-shrink-0">{u.name?.[0]}</div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white truncate">{u.name}</div>
                      <div className="text-[10px] text-[#A1A1AA] truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => approveAs(u.user_id, "player")} className="btn-primary text-[10px] px-2 py-1.5 flex items-center gap-1" data-testid={`approve-pl-${u.user_id}`}>
                      <UserCheck size={10}/> Jugador
                    </button>
                    <button onClick={() => approveAs(u.user_id, "coach")} className="btn-ghost text-[10px] px-2 py-1.5 border border-[#007AFF] text-[#007AFF]" data-testid={`approve-co-${u.user_id}`}>
                      Coach
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nuevos jugadores — aprobados sin categoría asignada. Visible para admin y coaches */}
        {uncategorized.length > 0 && (
          <div className="tactical-card p-5 mb-6" style={{ borderColor: "#22d3ee", background: "linear-gradient(120deg, rgba(34,211,238,0.06) 0%, transparent 50%)" }} data-testid="uncategorized-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <UserPlus size={16} className="text-cyan-400"/>
                <div className="overline text-cyan-400">Nuevos jugadores ({uncategorized.length})</div>
              </div>
              <div className="text-[10px] text-[#71717A] uppercase tracking-widest">Asigna categoría para incorporarlos al plantel</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {uncategorized.map(u => (
                <NewPlayerCard
                  key={u.user_id}
                  player={u}
                  isAdmin={isAdminLike(user?.role)}
                  coachCategories={user?.coach_categories || []}
                  onSaved={loadPlayers}
                />
              ))}
            </div>
          </div>
        )}

        {/* Category filter */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#27272A] mb-6">
          <FilterCell active={filter==="all"} onClick={()=>setFilter("all")} label="Todos" value={counts.all} testid="filter-all"/>
          <FilterCell active={filter==="tackle"} onClick={()=>setFilter("tackle")} label="Tackle" value={counts.tackle} testid="filter-tackle"/>
          <FilterCell active={filter==="flag_masculino"} onClick={()=>setFilter("flag_masculino")} label="Flag Masculino" value={counts.flag_masculino} testid="filter-flag-masc"/>
          <FilterCell active={filter==="flag_femenino"} onClick={()=>setFilter("flag_femenino")} label="Flag Femenino" value={counts.flag_femenino} testid="filter-flag-fem"/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.length === 0 && <div className="md:col-span-4 tactical-card p-10 text-center text-[#71717A]">No hay jugadores en esta categoría.</div>}
          {filtered.map(p => (
            <RosterCard
              key={p.user_id}
              player={p}
              photo={photos[p.user_id]}
              canEdit={canEdit}
              canDelete={canDelete}
              canView={canViewProfile(p)}
              isAdmin={isAdminLike(user?.role)}
              coachCategories={user?.coach_categories || []}
              onOpen={() => openProfile(p)}
              onDelete={() => deletePlayer(p.user_id, p.name)}
              onSaved={(profileUpdate) =>
                setPlayers(prev => prev.map(pp =>
                  pp.user_id === p.user_id ? { ...pp, profile: { ...(pp.profile || {}), ...profileUpdate } } : pp
                ))
              }
            />
          ))}
        </div>
        <div className="h-20 md:h-6"/>
      </div>
      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer}
          photoUrl={photos[selectedPlayer.user_id]}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </Layout>
  );
}

const FilterCell = ({ active, onClick, label, value, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`p-5 text-left border-r border-[#27272A] last:border-r-0 transition-colors ${active ? "bg-[#1A1A1A]" : "hover:bg-[#121212]"}`}
  >
    <div className={`overline ${active ? "text-[#007AFF]" : "text-[#71717A]"}`}>{label}</div>
    <div className={`font-display text-3xl mt-1 ${active ? "text-white" : "text-[#A1A1AA]"}`}>{value}</div>
  </button>
);

function NewPlayerCard({ player: u, isAdmin, coachCategories, onSaved }) {
  // Coach: solo puede asignar categorías dentro de su scope.
  // Admin: cualquier categoría.
  const allowedCats = isAdmin
    ? Object.keys(CAT_LABELS)
    : (coachCategories || []).filter(c => CAT_LABELS[c]);
  const [cats, setCats] = useState([]);
  const [position, setPosition] = useState("");
  const [jersey, setJersey] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (c) => setCats(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const positions = useMemo(() => {
    if (cats.length === 1) return validPositionsForCategory(cats[0]) || [];
    if (cats.length > 1) return validPositionsFor(cats);
    return [];
  }, [cats]);

  const save = async () => {
    if (cats.length === 0) {
      toast.error("Selecciona al menos una categoría");
      return;
    }
    setSaving(true);
    try {
      // 1) Categorías (endpoint actual, hace scope check coach)
      await api.patch(`/players/${u.user_id}/categories`, { categories: cats });
      // 2) Posición / jersey opcionales — endpoint coach-edit que sólo aplica
      //    overrides per_category que el coach tenga en scope.
      const perCat = {};
      cats.forEach(c => {
        if (!isAdmin && !coachCategories?.includes(c)) return;
        const sub = {};
        if (jersey !== "") sub.jersey_number = Number(jersey);
        if (position) sub.position = position;
        if (Object.keys(sub).length) perCat[c] = sub;
      });
      const profileBody = {};
      if (isAdmin && jersey !== "") profileBody.jersey_number = Number(jersey);
      if (isAdmin && position) profileBody.position = position;
      if (Object.keys(perCat).length) profileBody.per_category = perCat;
      if (Object.keys(profileBody).length) {
        await api.patch(`/players/${u.user_id}/profile`, profileBody);
      }
      toast.success(`${u.name} asignado a ${cats.map(c => CAT_LABELS[c]).join(", ")}`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al asignar categoría");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-3 space-y-3" data-testid={`new-player-${u.user_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        {u.picture ? (
          <img src={u.picture} alt="" className="w-10 h-10 object-cover flex-shrink-0"/>
        ) : (
          <div className="w-10 h-10 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white font-bold flex-shrink-0">{u.name?.[0]}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-white truncate">{u.name}</div>
          <div className="text-[10px] text-[#A1A1AA] truncate">{u.email}</div>
        </div>
      </div>

      <div>
        <div className="overline mb-1.5">Categoría</div>
        {allowedCats.length === 0 ? (
          <div className="text-[10px] text-[#FF9F0A]">Sin categorías asignadas como coach. Pide a un admin que te asigne categorías para gestionar nuevos jugadores.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {allowedCats.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => toggle(c)}
                data-testid={`new-cat-${u.user_id}-${c}`}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold border ${cats.includes(c) ? "border-[#007AFF] bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA]"}`}
              >
                {CAT_LABELS[c]}
              </button>
            ))}
          </div>
        )}
      </div>

      {cats.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] text-[#71717A] uppercase tracking-wider">Jersey (opc.)</label>
            <input
              type="number"
              min="0" max="99"
              value={jersey}
              onChange={e => setJersey(e.target.value)}
              className="input-tactical input-tactical-compact w-full text-sm"
              placeholder="—"
              data-testid={`new-jersey-${u.user_id}`}
            />
          </div>
          <div>
            <label className="text-[9px] text-[#71717A] uppercase tracking-wider">Posición (opc.)</label>
            <select
              value={position}
              onChange={e => setPosition(e.target.value)}
              className="input-tactical input-tactical-compact w-full text-sm"
              data-testid={`new-pos-${u.user_id}`}
            >
              <option value="">—</option>
              {positions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving || cats.length === 0 || allowedCats.length === 0}
          className="btn-primary text-[10px] px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`new-save-${u.user_id}`}
        >
          {saving ? "GUARDANDO..." : "ASIGNAR"}
        </button>
      </div>
    </div>
  );
}

function RosterCard({ player: p, photo, canEdit, canDelete, canView, isAdmin, coachCategories, onOpen, onDelete, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [cats, setCats] = useState(p.profile?.categories || []);
  // Status global y per_category editable
  const [status, setStatus] = useState(p.profile?.status || "active");
  // jersey/posiciones top-level (admin-only)
  const [topJersey, setTopJersey] = useState(p.profile?.jersey_number ?? "");
  const [topPos, setTopPos] = useState(p.profile?.position || "");
  const [topPos2, setTopPos2] = useState(p.profile?.position_secondary || "");
  // per_category overrides: { tackle: {jersey_number, position, position_secondary}, ... }
  const [perCat, setPerCat] = useState(() => p.profile?.per_category || {});

  const toggle = (c) => setCats(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  const updatePerCat = (cat, field, value) => {
    setPerCat(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), [field]: value } }));
  };

  // Categorías sobre las que el coach puede editar overrides (admin = todas)
  const editableCats = useMemo(() => {
    const playerCats = p.profile?.categories || [];
    if (isAdmin) return playerCats;
    return playerCats.filter(c => (coachCategories || []).includes(c));
  }, [p.profile?.categories, isAdmin, coachCategories]);

  const save = async () => {
    try {
      // 1. Categorías (endpoint existente, admin/coach con scope)
      if (JSON.stringify(cats) !== JSON.stringify(p.profile?.categories || [])) {
        await api.patch(`/players/${p.user_id}/categories`, { categories: cats });
      }
      // 2. Atributos tácticos via nuevo endpoint coach_edit_player
      const body = { status };
      if (isAdmin) {
        body.jersey_number = topJersey === "" ? null : Number(topJersey);
        body.position = topPos || null;
        body.position_secondary = topPos2 || null;
      }
      // per_category: solo enviar las categorías editables (el backend filtra igual)
      const pcPayload = {};
      for (const c of editableCats) {
        const cur = perCat[c] || {};
        pcPayload[c] = {
          jersey_number: cur.jersey_number === "" || cur.jersey_number === undefined ? null : Number(cur.jersey_number),
          position: cur.position ?? null,
          position_secondary: cur.position_secondary ?? null,
        };
      }
      if (Object.keys(pcPayload).length) body.per_category = pcPayload;
      const r = await api.patch(`/players/${p.user_id}/profile`, body);
      toast.success("Jugador actualizado");
      onSaved({ categories: cats, ...(r.data?.profile || {}) });
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  return (
    <div
      className="tactical-card p-5"
      data-testid={`player-card-${p.user_id}`}
      style={p.delinquency?.is_delinquent ? { borderColor: "#FF3B30", boxShadow: "inset 0 0 0 1px rgba(255,59,48,0.35)" } : undefined}
    >
      {p.delinquency?.is_delinquent && (
        <div
          className="flex items-center gap-2 mb-3 -mt-1 px-2.5 py-1 border border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]"
          data-testid={`moroso-badge-${p.user_id}`}
          title={`Cuotas adeudadas: ${(p.delinquency.overdue_months || []).join(", ")}`}
        >
          <AlertOctagon size={12}/>
          <span className="text-[10px] font-bold uppercase tracking-wider">
            Moroso · {p.delinquency.months_overdue} {p.delinquency.months_overdue === 1 ? "mes" : "meses"}
          </span>
        </div>
      )}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={canView ? onOpen : undefined}
          className={`w-16 h-16 bg-[#0A0A0A] border border-[#27272A] overflow-hidden flex-shrink-0 transition-all ${canView ? "cursor-pointer hover:border-cyan-400" : "cursor-default"}`}
          aria-label={canView ? `Ver perfil de ${p.name}` : undefined}
          disabled={!canView}
          data-testid={`open-profile-photo-${p.user_id}`}
        >
          {photo ? <img src={photo} alt="" className="w-full h-full object-cover"/>
            : p.picture ? <img src={p.picture} alt="" className="w-full h-full object-cover"/>
            : <div className="w-full h-full flex items-center justify-center font-display text-2xl text-[#27272A]">{p.name?.[0]}</div>}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            {canView ? (
              <button
                type="button"
                onClick={onOpen}
                className="font-display text-xl text-white truncate flex-1 text-left hover:text-cyan-400 transition-colors"
                data-testid={`open-profile-${p.user_id}`}
              >
                {p.name?.toUpperCase()}
              </button>
            ) : (
              <div className="font-display text-xl text-white truncate flex-1">{p.name?.toUpperCase()}</div>
            )}
            {(() => {
              const age = calcAge(p.profile?.birth_date);
              return age != null ? (
                <div
                  className="flex items-center gap-1 px-2 py-0.5 border border-[#27272A] text-[#A1A1AA] flex-shrink-0"
                  title={`Edad: ${age} años`}
                  data-testid={`age-${p.user_id}`}
                >
                  <Cake size={11} className="text-cyan-400"/>
                  <span className="font-mono text-xs"><strong className="text-white">{age}</strong> <span className="text-[10px] uppercase tracking-wider text-[#71717A]">años</span></span>
                </div>
              ) : null;
            })()}
            {canDelete && (
              <button onClick={onDelete} className="text-[#71717A] hover:text-[#FF3B30] flex-shrink-0" title="Eliminar jugador" data-testid={`delete-player-${p.user_id}`}>
                <Trash2 size={14}/>
              </button>
            )}
          </div>
          <div className="text-xs text-[#A1A1AA] truncate">{p.email}</div>
          <div className="flex gap-1 mt-2 flex-wrap items-center">
            {(p.profile?.categories || []).map(c => (
              <span key={c} className="badge badge-coach text-[10px] whitespace-nowrap">{CAT_LABELS[c]}</span>
            ))}
            {p.profile?.position && <span className="badge text-[10px] whitespace-nowrap">{p.profile.position}</span>}
            {!(p.profile?.categories || []).length && <span className="badge badge-pending text-[10px] whitespace-nowrap">Sin categoría</span>}
            {canEdit && !editing && (
              <button
                onClick={() => {
                  setCats(p.profile?.categories || []);
                  setStatus(p.profile?.status || "active");
                  setTopJersey(p.profile?.jersey_number ?? "");
                  setTopPos(p.profile?.position || "");
                  setTopPos2(p.profile?.position_secondary || "");
                  setPerCat(p.profile?.per_category || {});
                  setEditing(true);
                }}
                className="text-[#71717A] hover:text-[#007AFF] ml-1"
                data-testid={`edit-cats-${p.user_id}`}
                title="Editar jugador"
              >
                <Pencil size={12}/>
              </button>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-[#27272A] space-y-4">
          {/* Estado del jugador */}
          <div>
            <div className="overline mb-2">Estado</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_LIST.map(s => {
                const Icon = s.icon;
                const active = status === s.value;
                return (
                  <button
                    key={s.value}
                    onClick={() => setStatus(s.value)}
                    data-testid={`status-${p.user_id}-${s.value}`}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border transition-colors whitespace-nowrap ${active ? "border-white text-white" : "border-[#27272A] text-[#A1A1AA] hover:border-[#52525b]"}`}
                    style={active ? { background: `${s.color}22`, borderColor: s.color, color: s.color } : undefined}
                  >
                    <Icon size={10}/> {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Categorías (solo admin + coaches; coaches solo de su scope) */}
          {isAdmin && (
            <div>
              <div className="overline mb-2">Categorías</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(CAT_LABELS).map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => toggle(k)}
                    data-testid={`cat-btn-${p.user_id}-${k}`}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold border ${cats.includes(k) ? "border-[#007AFF] bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA]"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Jersey + posiciones POR CATEGORÍA (solo las que el coach administra) */}
          {editableCats.length > 0 && (
            <div className="space-y-3">
              <div className="overline">
                Datos por categoría
                {!isAdmin && <span className="text-[9px] ml-2 text-[#52525b]">(solo tus categorías)</span>}
              </div>
              {editableCats.map(c => {
                const cur = perCat[c] || {};
                const validPos = validPositionsForCategory(c) || [];
                return (
                  <div key={c} className="bg-[#0A0A0A] border border-[#27272A] p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-400">{CAT_LABELS[c]}</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[9px] text-[#71717A] uppercase tracking-wider">Jersey</label>
                        <input
                          type="number"
                          value={cur.jersey_number ?? ""}
                          onChange={(e) => updatePerCat(c, "jersey_number", e.target.value)}
                          className="input-tactical input-tactical-compact w-full text-sm"
                          placeholder="—"
                          min="0" max="99"
                          data-testid={`pc-jersey-${p.user_id}-${c}`}
                        />
                      </div>
                      <div>
                        <label className="text-[9px] text-[#71717A] uppercase tracking-wider">Posición</label>
                        <select
                          value={cur.position || ""}
                          onChange={(e) => updatePerCat(c, "position", e.target.value)}
                          className="input-tactical input-tactical-compact w-full text-sm"
                          data-testid={`pc-pos-${p.user_id}-${c}`}
                        >
                          <option value="">—</option>
                          {validPos.map(pos => <option key={pos.value} value={pos.value}>{pos.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[9px] text-[#71717A] uppercase tracking-wider">Pos. sec.</label>
                        <select
                          value={cur.position_secondary || ""}
                          onChange={(e) => updatePerCat(c, "position_secondary", e.target.value)}
                          className="input-tactical input-tactical-compact w-full text-sm"
                          data-testid={`pc-pos2-${p.user_id}-${c}`}
                        >
                          <option value="">—</option>
                          {validPos.map(pos => <option key={pos.value} value={pos.value}>{pos.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Jersey y posiciones GLOBALES (top-level) — solo admin */}
          {isAdmin && (
            <div className="space-y-2">
              <div className="overline">Datos globales (default)</div>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" min="0" max="99" placeholder="Jersey" value={topJersey} onChange={(e) => setTopJersey(e.target.value)} className="input-tactical input-tactical-compact text-sm" data-testid={`top-jersey-${p.user_id}`}/>
                <input type="text" placeholder="Posición" value={topPos} onChange={(e) => setTopPos(e.target.value)} className="input-tactical input-tactical-compact text-sm" data-testid={`top-pos-${p.user_id}`}/>
                <input type="text" placeholder="Pos. sec." value={topPos2} onChange={(e) => setTopPos2(e.target.value)} className="input-tactical input-tactical-compact text-sm" data-testid={`top-pos2-${p.user_id}`}/>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setEditing(false)} className="btn-ghost text-[10px]" data-testid={`cancel-edit-${p.user_id}`}>CANCELAR</button>
            <button onClick={save} className="btn-primary text-[10px] px-3 py-1.5" data-testid={`save-edit-${p.user_id}`}>GUARDAR</button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#27272A] grid grid-cols-3 gap-2 text-center">
        <div><div className="overline">#</div><div className="font-display text-xl text-white">{p.profile?.jersey_number ?? "—"}</div></div>
        <div><div className="overline">Alt</div><div className="font-display text-xl text-white">{p.profile?.height_cm ?? "—"}</div></div>
        <div><div className="overline">Peso</div><div className="font-display text-xl text-white">{p.profile?.weight_kg ?? "—"}</div></div>
      </div>
    </div>
  );
}


function AddPlayerForm({ onClose, onSaved }) {
  const [data, setData] = useState({
    name: "", email: "", role: "player",
    jersey_number: "", position: "",
    categories: [], status: "active",
  });
  const [saving, setSaving] = useState(false);

  const toggleCat = (c) => setData(d => ({
    ...d,
    categories: d.categories.includes(c) ? d.categories.filter(x => x !== c) : [...d.categories, c],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data };
      payload.jersey_number = payload.jersey_number ? Number(payload.jersey_number) : null;
      await api.post("/users/manual", payload);
      toast.success("Jugador creado");
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al crear";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="tactical-card p-6 w-full max-w-2xl my-8"
        data-testid="add-player-form"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline">Nuevo Miembro</div>
            <div className="font-display text-2xl text-white">AGREGAR JUGADOR</div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost text-xs">✕</button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-tactical">Nombre completo *</label>
            <input required className="input-tactical" value={data.name} onChange={e=>setData({...data, name: e.target.value})} data-testid="ap-name"/>
          </div>
          <div>
            <label className="label-tactical">Email *</label>
            <input required type="email" className="input-tactical" value={data.email} onChange={e=>setData({...data, email: e.target.value})} data-testid="ap-email"/>
            <div className="text-[10px] text-[#71717A] mt-1">Debe ser único. Si después hace login con Google con este mismo email, se vincula.</div>
          </div>
          <div>
            <label className="label-tactical">Rol</label>
            <select className="input-tactical" value={data.role} onChange={e=>setData({...data, role: e.target.value})} data-testid="ap-role">
              <option value="player">Jugador</option>
              <option value="coach">Coach</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="label-tactical">Estado</label>
            <select className="input-tactical" value={data.status} onChange={e=>setData({...data, status: e.target.value})}>
              <option value="active">Activo</option>
              <option value="injured">Lesionado</option>
              <option value="suspended">Suspendido</option>
            </select>
          </div>
          <div>
            <label className="label-tactical">Número camiseta</label>
            <input type="number" className="input-tactical" value={data.jersey_number} onChange={e=>setData({...data, jersey_number: e.target.value})} data-testid="ap-jersey"/>
          </div>
          <div>
            <label className="label-tactical">Posición</label>
            <select className="input-tactical" value={data.position} onChange={e=>setData({...data, position: e.target.value})} data-testid="ap-position">
              <option value="">Selecciona...</option>
              {validPositionsFor(data.categories).map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-tactical">Categorías</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "tackle", label: "Tackle" },
                { key: "flag_masculino", label: "Flag Masc." },
                { key: "flag_femenino", label: "Flag Fem." },
              ].map(c => {
                const active = data.categories.includes(c.key);
                return (
                  <button
                    type="button"
                    key={c.key}
                    onClick={() => toggleCat(c.key)}
                    data-testid={`ap-cat-${c.key}`}
                    className={`px-3 py-2 border text-xs uppercase tracking-wider font-bold transition-colors ${active ? "border-[#007AFF] bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#27272A]">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">CANCELAR</button>
          <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="ap-submit">
            {saving ? "GUARDANDO..." : "CREAR JUGADOR"}
          </button>
        </div>
      </form>
    </div>
  );
}
